import { useCallback, useEffect, useRef, useState } from "react";
import { Storage } from "aws-amplify";
import {
  RiRefreshLine,
  RiErrorWarningLine,
  RiSearchLine,
  RiInboxLine,
  RiArrowUpSFill,
  RiArrowDownSFill,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiImageEditLine,
  RiImageAddLine,
} from "react-icons/ri";
import { MdDriveFolderUpload } from "react-icons/md";
import { format } from "date-fns";
import { brandOptions, MediaUri } from "../../constants/enum";
import {
  searchProducts,
  SEARCH_WINDOW,
  ProductSortField,
} from "../../apis/product";
import { ProductLibProduct } from "../../API";
import ModalConfirm from "../../components/ModalConfirm";
import MatcodeUploadModal from "./MatcodeUploadModal";
import UploadTab from "./UploadTab";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const dateFormat = "dd-MM-yyyy (HH:mm:ss)";

// Photos live at files/thumbnail/<matcode>/<file>; served via productlibrary-media
// as `${MediaUri}/thumbnail/<matcode>/<file>` (same as the single-product page).
const PK_CS_PHOTO_PREFIX = "files/thumbnail";

// Inline gray "no image" placeholder shown until a real thumbnail resolves.
const DEFAULT_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'>` +
      `<rect width='24' height='24' rx='3' fill='#f1f5f9'/>` +
      `<circle cx='8.5' cy='9' r='1.6' fill='#cbd5e1'/>` +
      `<path d='M4 17l5-5 4 4 3-3 4 4' fill='none' stroke='#cbd5e1' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/>` +
      `</svg>`
  );

// Finds the first image under files/thumbnail/<matcode> and returns its
// productlibrary-media URL (null when the matcode has no uploaded photo).
async function resolveThumbnail(matcode: string): Promise<string | null> {
  try {
    const res: any = await Storage.list(`${PK_CS_PHOTO_PREFIX}/${matcode}/`, {
      level: "public",
      pageSize: 10,
    });
    const img = (res.results ?? []).find(
      (o: any) => o.key && o.size > 0 && /\.(jpe?g|png)$/i.test(o.key)
    );
    if (!img) return null;
    const path = img.key.replace(`${PK_CS_PHOTO_PREFIX}/`, "");
    return `${MediaUri}/thumbnail/${path}`;
  } catch {
    return null;
  }
}

// Same as resolveThumbnail but polls a few times — thumbnails are generated
// asynchronously by the resize Lambda, so a just-uploaded matcode may return
// nothing on the first try.
async function resolveThumbnailWithRetry(
  matcode: string,
  tries = 5,
  delayMs = 2000
): Promise<string | null> {
  for (let i = 0; i < tries; i++) {
    const url = await resolveThumbnail(matcode);
    if (url) return url;
    if (i < tries - 1)
      await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return null;
}

type SortState = { key: ProductSortField; direction: "asc" | "desc" };

function ProductTab() {
  const [brand, setBrand] = useState<string>(""); // "" = all brands
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState<SortState>({
    key: "packageCaseUpdatedAt",
    direction: "desc",
  });
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState<ProductLibProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [uploadMatcode, setUploadMatcode] = useState<string | null>(null);
  const [showBatchUpload, setShowBatchUpload] = useState(false);

  // Resolved thumbnail URLs keyed by matcode (null = no photo), cached across pages.
  const [thumbs, setThumbs] = useState<Record<string, string | null>>({});
  const thumbRequested = useRef<Set<string>>(new Set());

  // Reset to the first page (used whenever the query changes).
  const resetPaging = useCallback(() => {
    setPageIndex(0);
  }, []);

  // Commit the search term -> fresh server query from the first page. While a
  // term is active, order by materialCode so prefix matches list in sequence
  // (otherwise a broad prefix scatters matches across pages); revert on clear.
  const commitSearch = useCallback(
    (term: string) => {
      const q = term.trim();
      setDebouncedSearch(q);
      setSort(
        q
          ? { key: "materialCode", direction: "asc" }
          : { key: "packageCaseUpdatedAt", direction: "desc" }
      );
      resetPaging();
    },
    [resetPaging]
  );

  // Debounce the search box; committing a new term also resets paging so the
  // fetch fires once (same render batch as the term change).
  useEffect(() => {
    const id = setTimeout(() => commitSearch(search), 400);
    return () => clearTimeout(id);
  }, [search, commitSearch]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items, total: t } = await searchProducts({
        brand,
        search: debouncedSearch,
        page: pageIndex + 1,
        pageSize,
        sortField: sort.key,
        sortDirection: sort.direction,
      });
      setRows(items);
      setTotal(t);
    } catch (err: any) {
      // packageCaseUpdatedAt may be unmapped in OpenSearch when no product has a
      // value yet -> sorting by it errors. Fall back to updatedAt automatically.
      if (sort.key === "packageCaseUpdatedAt") {
        resetPaging();
        setSort({ key: "updatedAt", direction: "desc" });
        return;
      }
      setError(err?.errors?.[0]?.message ?? err?.message ?? "โหลดข้อมูลไม่สำเร็จ");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [brand, debouncedSearch, pageIndex, pageSize, sort, resetPaging]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // After any upload/photo change: re-fetch the current page (row order /
  // timestamps), then keep retrying the affected matcodes' thumbnails until the
  // resize Lambda has generated them.
  const refreshAfterUpload = useCallback(
    (codes: string[] = []) => {
      thumbRequested.current.clear();
      setThumbs({});
      loadPage();
      codes.forEach(async (code) => {
        const url = await resolveThumbnailWithRetry(code);
        if (url) {
          thumbRequested.current.add(code);
          setThumbs((prev) => ({ ...prev, [code]: url }));
        }
      });
    },
    [loadPage]
  );

  // Lazily resolve thumbnails for the rows currently on screen. Results are keyed
  // by matcode so applying them unconditionally is safe (no cancelled guard —
  // that trips React StrictMode's double-invoke and drops the first run's fetch).
  useEffect(() => {
    rows.forEach(async (row) => {
      const code = row.materialCode;
      if (!code || thumbRequested.current.has(code)) return;
      thumbRequested.current.add(code);
      const url = await resolveThumbnail(code);
      setThumbs((prev) => ({ ...prev, [code]: url }));
    });
  }, [rows]);

  // Page count, capped to the OpenSearch result window (from + size ≤ WINDOW).
  const windowPages = Math.max(1, Math.floor(SEARCH_WINDOW / pageSize));
  const totalPages = Math.min(
    Math.max(1, Math.ceil(total / pageSize)),
    windowPages
  );

  const toggleSort = (key: ProductSortField) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
    resetPaging();
  };

  const SortHeader = ({
    label,
    field,
    className,
  }: {
    label: string;
    field: ProductSortField;
    className?: string;
  }) => (
    <th className={`px-4 py-3 font-medium ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className="inline-flex items-center gap-0.5 hover:text-gray-700"
      >
        {label}
        {sort.key === field &&
          (sort.direction === "asc" ? (
            <RiArrowUpSFill size={16} className="text-blue-500" />
          ) : (
            <RiArrowDownSFill size={16} className="text-blue-500" />
          ))}
      </button>
    </th>
  );

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Brand</label>
          <select
            value={brand}
            onChange={(e) => {
              setBrand(e.target.value);
              resetPaging();
            }}
            disabled={loading}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50"
          >
            <option value="">All</option>
            {brandOptions.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>

          <div className="relative">
            <RiSearchLine
              size={15}
              className="absolute -translate-y-1/2 left-2.5 top-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitSearch(search);
              }}
              placeholder="ค้นหา ชื่อ / รหัสสินค้า…"
              className="w-64 rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-700 shadow-sm focus:border-blue-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          <button
            type="button"
            onClick={() => setShowBatchUpload(true)}
            title="อัปโหลดหลายโฟลเดอร์พร้อมกัน"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:shadow-md"
          >
            <MdDriveFolderUpload size={18} />
            อัพโหลดหลาย MatCode(s)
          </button>
          <button
            type="button"
            onClick={loadPage}
            disabled={loading}
            title="โหลดข้อมูลใหม่"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RiRefreshLine size={16} className={loading ? "animate-spin" : ""} />
            โหลดใหม่
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 text-sm border rounded-lg border-red-200 bg-red-50 text-red-600">
          <RiErrorWarningLine size={16} /> {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50">
                <th className="w-10 px-4 py-3 font-medium text-right">#</th>
                <th className="w-20 px-4 py-3 font-medium text-center">รูปภาพ</th>
                <SortHeader
                  label="MatCode"
                  field="materialCode"
                  className="whitespace-nowrap"
                />
                <SortHeader label="ชื่อ" field="name" />
                <SortHeader
                  label="อัปเดตรูปล่าสุด"
                  field="packageCaseUpdatedAt"
                  className="whitespace-nowrap"
                />
                <th className="w-28 px-4 py-3 font-medium text-center whitespace-nowrap" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, i) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-right text-gray-400">
                    {pageIndex * pageSize + i + 1}
                  </td>
                  <td className="px-4 py-2">
                    <div className="w-12 h-12 overflow-hidden border border-gray-200 rounded-md bg-gray-50 aspect-square shrink-0">
                      <img
                        src={thumbs[row.materialCode] || DEFAULT_IMAGE}
                        alt={row.materialCode}
                        loading="lazy"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            DEFAULT_IMAGE;
                        }}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">
                    {row.materialCode}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    <div className="max-w-[320px] truncate" title={row.name}>
                      {row.name}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                    {row.packageCaseUpdatedAt
                      ? format(new Date(row.packageCaseUpdatedAt), dateFormat)
                      : "-"}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {(() => {
                      const hasImage = !!thumbs[row.materialCode];
                      return (
                        <button
                          type="button"
                          onClick={() => setUploadMatcode(row.materialCode)}
                          title={
                            hasImage
                              ? `แก้ไขรูปสำหรับ ${row.materialCode}`
                              : `อัปโหลดรูปสำหรับ ${row.materialCode}`
                          }
                          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium ${
                            hasImage
                              ? "border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200"
                              : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                          }`}
                        >
                          {hasImage ? (
                            <RiImageEditLine size={14} />
                          ) : (
                            <RiImageAddLine size={14} />
                          )}
                          {hasImage ? "แก้ไขรูป" : "อัปโหลดรูป"}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}

              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <RiInboxLine
                      size={32}
                      className="mx-auto mb-2 text-gray-300"
                    />
                    <p className="text-sm text-gray-500">
                      {debouncedSearch.trim()
                        ? `ไม่พบสินค้าที่ตรงกับ "${debouncedSearch.trim()}"`
                        : "ไม่พบสินค้า"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
            <RiRefreshLine size={16} className="animate-spin" /> กำลังโหลด…
          </div>
        )}
      </div>

      {/* Footer: pagination */}
      <div className="flex flex-wrap items-center justify-end gap-3 mt-4 text-xs text-gray-500">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span>ต่อหน้า</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                resetPaging();
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-400 focus:outline-none"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex <= 0 || loading}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <RiArrowLeftSLine size={18} />
            </button>
            <span className="px-1 tabular-nums">
              หน้า {(pageIndex + 1).toLocaleString()} /{" "}
              {totalPages.toLocaleString()}
            </span>
            <button
              type="button"
              onClick={() =>
                setPageIndex((i) => Math.min(totalPages - 1, i + 1))
              }
              disabled={pageIndex >= totalPages - 1 || loading}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <RiArrowRightSLine size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* (3) Per-matcode photo management (view / upload / delete) */}
      <MatcodeUploadModal
        matcode={uploadMatcode}
        onClose={() => setUploadMatcode(null)}
        onUploaded={(code) => refreshAfterUpload([code])}
      />

      {/* (4) Batch upload — reuses the UPLOAD tab inside a modal */}
      <ModalConfirm
        show={showBatchUpload}
        onHide={() => setShowBatchUpload(false)}
        title="อัพโหลดหลาย MatCode(s)"
        showCloseIcon
        maxWidth="max-w-4xl"
      >
        <div className="h-[75vh] overflow-y-auto">
          <UploadTab
            withAccount={false}
            stickyActionBar={false}
            onUploaded={(codes) => refreshAfterUpload(codes)}
          />
        </div>
      </ModalConfirm>
    </div>
  );
}

export default ProductTab;
